/**
 * VoiceSelectorModal -- Placeholder modal for voice selection UI.
 *
 * Future: Will integrate with Eleven Labs API for voice selection.
 * Currently shows a "coming soon" placeholder with the current voice name.
 */

import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'

interface VoiceSelectorModalProps {
  isOpen: boolean
  onClose: () => void
  currentVoiceName?: string
  onSelectVoice?: (voiceId: string, voiceName: string) => void
}

export const VoiceSelectorModal = memo(function VoiceSelectorModal({
  isOpen,
  onClose,
  currentVoiceName,
}: VoiceSelectorModalProps) {
  const { t } = useTranslation('canvas')
  if (!isOpen) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-8"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-surface-0 border border-border rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
          <h2 className="text-sm font-semibold text-foreground">
            {t('voiceSelectorModal.selectVoice')}
          </h2>
          <button
            onClick={onClose}
            className="w-6 h-6 rounded flex items-center justify-center text-text-muted hover:text-foreground hover:bg-surface-2 transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-surface-2 flex items-center justify-center mb-4">
              <svg
                className="w-6 h-6 text-text-muted"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                />
              </svg>
            </div>
            <p className="text-sm text-foreground font-medium mb-1">
              {t('voiceSelectorModal.voiceSelection')}
            </p>
            <p className="text-xs font-medium text-text-muted mb-4">
              {t('voiceSelectorModal.comingSoon')}
            </p>

            {currentVoiceName && (
              <div className="px-3 py-1.5 bg-surface-1 rounded-lg border border-border-subtle">
                <span className="text-xs font-medium text-text-muted">
                  {t('voiceSelectorModal.current')}{' '}
                </span>
                <span className="text-xs font-medium text-foreground">
                  {currentVoiceName}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-border-subtle">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-medium text-foreground bg-surface-1 hover:bg-surface-2 border border-border rounded-lg transition-colors"
          >
            {t('voiceSelectorModal.close')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
})

VoiceSelectorModal.displayName = 'VoiceSelectorModal'
